import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, onSnapshot, doc, getDoc, updateDoc } from 'firebase/firestore';
import { database, auth } from './firebaseConfig';
import { Calendar, Clock, Users, TrendingUp, Building2, Wrench } from 'lucide-react';
import './AnaSayfa.css';
import SetupGuide from './SetupGuide';

const AnaSayfa = () => {
  const [stats, setStats] = useState({
    totalPuantajlar: 0,
    sonHaftaPuantajlar: 0,
    toplamSaat: 0,
    sonHaftaSaat: 0,
    toplamKontor: 0,
    sonHaftaKontor: 0,
    aktifOperatorler: 0,
    aktifMusteriler: 0,
    aktifMakineler: 0,
    bekleyenOnaylar: 0
  });
  const [loading, setLoading] = useState(true);
  const [sonPuantajlar, setSonPuantajlar] = useState([]);
  const [allPuantajlar, setAllPuantajlar] = useState([]);
  const [selectedDateFilter, setSelectedDateFilter] = useState('bugün');
  const [setupCompleted, setSetupCompleted] = useState(false);

  const parseDate = useCallback((dateString) => {
    if (!dateString) return new Date();
    const [day, month, year] = dateString.split('.');
    if (!day || !month || !year) return new Date();
    return new Date(`${year}-${month}-${day}`);
  }, []);

  const parseTime = useCallback((timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }, []);

  const calculateTotalHours = useCallback((puantaj) => {
    let totalHours = 0;
    
    // 1. vardiya
    if (puantaj['1. Başlangıç Saati'] && puantaj['1. Bitiş Saati']) {
      const start1 = parseTime(puantaj['1. Başlangıç Saati']);
      const end1 = parseTime(puantaj['1. Bitiş Saati']);
      if (start1 && end1) {
        totalHours += (end1 - start1) / (1000 * 60 * 60);
      }
    }
    
    // 2. vardiya
    if (puantaj['2. Başlangıç Saati'] && puantaj['2. Bitiş Saati']) {
      const start2 = parseTime(puantaj['2. Başlangıç Saati']);
      const end2 = parseTime(puantaj['2. Bitiş Saati']);
      if (start2 && end2) {
        totalHours += (end2 - start2) / (1000 * 60 * 60);
      }
    }
    
    return totalHours;
  }, [parseTime]);

  // Kontör hesaplama fonksiyonu (dakika bazında)
  const calculateTotalMinutes = useCallback((puantaj) => {
    let totalMinutes = 0;
    
    // 1. vardiya
    if (puantaj['1. Başlangıç Saati'] && puantaj['1. Bitiş Saati']) {
      const start1 = parseTime(puantaj['1. Başlangıç Saati']);
      const end1 = parseTime(puantaj['1. Bitiş Saati']);
      if (start1 && end1) {
        totalMinutes += (end1 - start1) / (1000 * 60);
      }
    }
    
    // 2. vardiya
    if (puantaj['2. Başlangıç Saati'] && puantaj['2. Bitiş Saati']) {
      const start2 = parseTime(puantaj['2. Başlangıç Saati']);
      const end2 = parseTime(puantaj['2. Bitiş Saati']);
      if (start2 && end2) {
        totalMinutes += (end2 - start2) / (1000 * 60);
      }
    }
    
    return Math.round(totalMinutes);
  }, [parseTime]);

  // Toplam çalışma süresini saat:dakika formatında döndüren fonksiyon
  const formatTotalWorkTime = useCallback((puantaj) => {
    let totalMinutes = 0;
    
    // 1. vardiya
    if (puantaj['1. Başlangıç Saati'] && puantaj['1. Bitiş Saati']) {
      const start1 = parseTime(puantaj['1. Başlangıç Saati']);
      const end1 = parseTime(puantaj['1. Bitiş Saati']);
      if (start1 && end1) {
        totalMinutes += (end1 - start1) / (1000 * 60);
      }
    }
    
    // 2. vardiya
    if (puantaj['2. Başlangıç Saati'] && puantaj['2. Bitiş Saati']) {
      const start2 = parseTime(puantaj['2. Başlangıç Saati']);
      const end2 = parseTime(puantaj['2. Bitiş Saati']);
      if (start2 && end2) {
        totalMinutes += (end2 - start2) / (1000 * 60);
      }
    }
    
    const totalMinutesRounded = Math.round(totalMinutes);
    const hours = Math.floor(totalMinutesRounded / 60);
    const minutes = totalMinutesRounded % 60;
    
    // Saat:dakika formatında döndür (örn: 01:30)
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }, [parseTime]);

  // En son bitiş saatini bulan fonksiyon (sıralama için)
  const getLatestEndTime = useCallback((puantaj) => {
    let latestEndTime = null;
    
    // 1. vardiya bitiş saati
    if (puantaj['1. Bitiş Saati']) {
      const end1 = parseTime(puantaj['1. Bitiş Saati']);
      if (end1) {
        latestEndTime = end1;
      }
    }
    
    // 2. vardiya bitiş saati (varsa ve daha geç ise)
    if (puantaj['2. Bitiş Saati']) {
      const end2 = parseTime(puantaj['2. Bitiş Saati']);
      if (end2 && (!latestEndTime || end2 > latestEndTime)) {
        latestEndTime = end2;
      }
    }
    
    return latestEndTime;
  }, [parseTime]);

  // Tarih filtresi seçenekleri
  const dateFilterOptions = [
    { value: 'bugün', label: 'Bugün', days: 1 },
    { value: 'son2gun', label: 'Son 2 Gün', days: 2 },
    { value: 'son3gun', label: 'Son 3 Gün', days: 3 },
    { value: 'son4gun', label: 'Son 4 Gün', days: 4 },
    { value: 'son5gun', label: 'Son 5 Gün', days: 5 },
    { value: 'son6gun', label: 'Son 6 Gün', days: 6 },
    { value: 'son7gun', label: 'Son 7 Gün', days: 7 },
    { value: 'son14gun', label: 'Son 14 Gün', days: 14 },
    { value: 'son30gun', label: 'Son 30 Gün', days: 30 }
  ];

  // Tarih filtresi fonksiyonu
  const getDateFilterRange = useCallback((filter) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Bugünün sonuna kadar dahil et
    
    const option = dateFilterOptions.find(opt => opt.value === filter);
    const days = option ? option.days : 1;
    
    if (days === 1) {
      // Sadece bugün için
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      return { start: startOfToday, end: today };
    } else {
      // Son X gün için
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - (days - 1));
      startDate.setHours(0, 0, 0, 0);
      return { start: startDate, end: today };
    }
  }, []);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      setLoading(false);
      return;
    }

    const userEmail = currentUser.email;
    
    // Mevcut verileri kontrol edip setup progress'i güncelle
    const checkAndUpdateSetupProgress = async () => {
      try {
        const userDocRef = doc(database, 'users', userEmail);
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists()) return;
        
        const userData = userDoc.data();
        const currentSetupProgress = userData.setupProgress || {};
        
        // Mevcut verileri kontrol et
        const checks = {
          machines: false,
          operators: false,
          customers: false,
          banks: currentSetupProgress.banks || false // Banka opsiyonel, mevcut değeri koru
        };
        
        // Firma bilgileri artık zorunlu değil, bu adım kaldırıldı.
 
        // 1. Makine kontrolü
        const machinesRef = collection(database, `users/${userEmail}/machines`);
        const machinesSnapshot = await getDocs(machinesRef);
        checks.machines = machinesSnapshot.size > 0;
        
        // 3. Operatör kontrolü
        const operatorsRef = collection(database, `users/${userEmail}/operators`);
        const operatorsSnapshot = await getDocs(operatorsRef);
        checks.operators = operatorsSnapshot.size > 0;
        
        // 4. Müşteri kontrolü
        const customersRef = collection(database, `users/${userEmail}/customerList`);
        const approvedCustomersQuery = query(customersRef, where('Onay', '==', 'Onaylandı'));
        const customersSnapshot = await getDocs(approvedCustomersQuery);
        checks.customers = customersSnapshot.size > 0;
        
        // Setup progress'i güncelle (sadece değişiklik varsa)
        const needsUpdate = Object.keys(checks).some(key => 
          currentSetupProgress[key] !== checks[key]
        );
        
        if (needsUpdate) {
          await updateDoc(userDocRef, {
            setupProgress: checks
          });
          
          console.log('🔄 Setup progress otomatik güncellendi:', {
            before: currentSetupProgress,
            after: checks,
            counts: {
              machines: machinesSnapshot.size,
              operators: operatorsSnapshot.size,
              customers: customersSnapshot.size
            }
          });
        }
      } catch (error) {
        console.error('Setup progress kontrolü sırasında hata:', error);
      }
    };
    
    // İlk kontrol
    checkAndUpdateSetupProgress();
    
    // Setup progress kontrolü - gerçek zamanlı dinleme
    const userDocRef = doc(database, 'users', userEmail);
    const unsubscribeSetup = onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        const setupProgress = doc.data().setupProgress || {};
        const isCompleted = setupProgress.machines && 
                          setupProgress.operators && 
                          setupProgress.customers;
        setSetupCompleted(isCompleted);
        
        console.log('Setup progress güncellendi:', {
          company: setupProgress.company,
          machines: setupProgress.machines,
          operators: setupProgress.operators,
          customers: setupProgress.customers,
          banks: setupProgress.banks,
          completed: isCompleted
        });
      }
    });
    
    // Son 1 hafta için tarih hesapla
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    // Real-time puantaj dinleyicisi
    const puantajlarRef = collection(database, `users/${userEmail}/timeSheets`);
    const unsubscribePuantajlar = onSnapshot(puantajlarRef, (snapshot) => {
      const puantajlarData = snapshot.docs.map(doc => doc.data());
      
      console.log('Puantaj verileri güncellendi:', puantajlarData.length);
      
      // Tüm puantajları sakla (filtreleme yapmadan)
      const validPuantajlar = puantajlarData
        .filter(puantaj => 
          puantaj['Müşteri Adı'] && 
          puantaj['Makine İsmi'] && 
          puantaj.Tarih && 
          puantaj['Operatör Adı'] &&
          (puantaj['1. Başlangıç Saati'] || puantaj['2. Başlangıç Saati'])
        )
        .sort((a, b) => {
          const dateA = parseDate(a.Tarih);
          const dateB = parseDate(b.Tarih);
          
          // Önce tarihe göre sırala (en yeni tarih en üstte)
          if (dateA.getTime() !== dateB.getTime()) {
            return dateB - dateA;
          }
          
          // Aynı tarihte ise bitiş saatine göre sırala (en geç biten en üstte)
          const endTimeA = getLatestEndTime(a);
          const endTimeB = getLatestEndTime(b);
          
          if (endTimeA && endTimeB) {
            return endTimeB - endTimeA;
          } else if (endTimeA) {
            return -1; // A'nın bitiş saati var, B'nin yok - A önce gelsin
          } else if (endTimeB) {
            return 1; // B'nin bitiş saati var, A'nın yok - B önce gelsin
          } else {
            return 0; // İkisinin de bitiş saati yok
          }
        });
      
      setAllPuantajlar(validPuantajlar);

      // Son hafta puantajlarını filtrele
      const sonHaftaPuantajlar = puantajlarData.filter(puantaj => {
        const puantajDate = parseDate(puantaj.Tarih);
        return puantajDate >= oneWeekAgo;
      });

      // Toplam saat hesapla
      const toplamSaat = puantajlarData.reduce((total, puantaj) => {
        return total + calculateTotalHours(puantaj);
      }, 0);

      // Son hafta saat hesapla
      const sonHaftaSaat = sonHaftaPuantajlar.reduce((total, puantaj) => {
        return total + calculateTotalHours(puantaj);
      }, 0);

      // Toplam kontör hesapla (dakika)
      const toplamKontor = puantajlarData.reduce((total, puantaj) => {
        return total + calculateTotalMinutes(puantaj);
      }, 0);

      // Son hafta kontör hesapla (dakika)
      const sonHaftaKontor = sonHaftaPuantajlar.reduce((total, puantaj) => {
        return total + calculateTotalMinutes(puantaj);
      }, 0);

      // Stats'i güncelle (sadece puantaj ile ilgili kısımları)
      setStats(prevStats => ({
        ...prevStats,
        totalPuantajlar: puantajlarData.length,
        sonHaftaPuantajlar: sonHaftaPuantajlar.length,
        toplamSaat: Math.round(toplamSaat * 10) / 10,
        sonHaftaSaat: Math.round(sonHaftaSaat * 10) / 10,
        toplamKontor: toplamKontor,
        sonHaftaKontor: sonHaftaKontor
      }));
      
      console.log('Ana sayfa stats güncellendi - Toplam puantaj:', puantajlarData.length);
    });

    // Real-time operatör dinleyicisi
    const operatorlerRef = collection(database, `users/${userEmail}/operators`);
    const unsubscribeOperatorler = onSnapshot(operatorlerRef, (snapshot) => {
      console.log('Operatör sayısı güncellendi:', snapshot.size);
      setStats(prevStats => ({
        ...prevStats,
        aktifOperatorler: snapshot.size
      }));
    });

    // Real-time müşteri dinleyicisi
    const musterilerRef = collection(database, `users/${userEmail}/customerList`);
    const unsubscribeMusteriler = onSnapshot(musterilerRef, (snapshot) => {
      console.log('Müşteri sayısı güncellendi:', snapshot.size);
      setStats(prevStats => ({
        ...prevStats,
        aktifMusteriler: snapshot.size
      }));
    });

    // Real-time bekleyen onaylar dinleyicisi
    const bekleyenOnaylarQuery = query(musterilerRef, where('Onay', '==', 'Onay Bekliyor'));
    const unsubscribeBekleyenOnaylar = onSnapshot(bekleyenOnaylarQuery, (snapshot) => {
      setStats(prevStats => ({
        ...prevStats,
        bekleyenOnaylar: snapshot.size
      }));
    });

    // Real-time makine dinleyicisi
    const makinelerRef = collection(database, `users/${userEmail}/machines`);
    const unsubscribeMakineler = onSnapshot(makinelerRef, (snapshot) => {
      console.log('Makine sayısı güncellendi:', snapshot.size);
      setStats(prevStats => ({
        ...prevStats,
        aktifMakineler: snapshot.size
      }));
    });

    // Loading'i false yap
    setLoading(false);

    // Cleanup fonksiyonu - tüm dinleyicileri temizle
    return () => {
      unsubscribeSetup();
      unsubscribePuantajlar();
      unsubscribeOperatorler();
      unsubscribeMusteriler();
      unsubscribeBekleyenOnaylar();
      unsubscribeMakineler();
    };
  }, [parseDate, calculateTotalHours, calculateTotalMinutes, getLatestEndTime]);

  // Tarih filtresine göre puantajları filtrele (ayrı useEffect)
  useEffect(() => {
    if (allPuantajlar.length === 0) {
      setSonPuantajlar([]);
      return;
    }

    const dateRange = getDateFilterRange(selectedDateFilter);
    const filteredPuantajlar = allPuantajlar.filter(puantaj => {
      const puantajDate = parseDate(puantaj.Tarih);
      // Başlangıç ve bitiş tarihleri dahil (inclusive)
      return puantajDate >= dateRange.start && puantajDate <= dateRange.end;
    });
    
    setSonPuantajlar(filteredPuantajlar);
  }, [allPuantajlar, selectedDateFilter, getDateFilterRange, parseDate]);

  if (loading) {
    return (
      <div className="ana-sayfa-loading">
        <div className="loading-spinner"></div>
        <p>Veriler yükleniyor...</p>
      </div>
    );
  }

  // Eğer kurulum tamamlanmamışsa sadece SetupGuide'ı göster
  if (!setupCompleted) {
    return (
      <div className="ana-sayfa setup-mode">
        <div className="setup-welcome">
          <h1>HOPS Sistemine Hoş Geldiniz!</h1>
          <p>Sistemi kullanmaya başlamak için lütfen aşağıdaki kurulum adımlarını tamamlayın.</p>
        </div>
        <SetupGuide userEmail={auth.currentUser?.email} />
      </div>
    );
  }

  // Kurulum tamamlandıysa normal içeriği göster
  return (
    <div className="ana-sayfa">
      <div className="ana-sayfa-header">
        <h1>Ana Sayfa</h1>
        <p>Hoş geldiniz! İşletmenizin genel durumunu buradan takip edebilirsiniz.</p>
      </div>

      <div className="recent-activities">
        <div className="recent-activities-header">
          <h2>Son Puantajlar {sonPuantajlar.length > 0 && `(${sonPuantajlar.length})`}</h2>
          <div className="date-filter-select">
            <select 
              value={selectedDateFilter} 
              onChange={(e) => setSelectedDateFilter(e.target.value)}
              className="date-filter-dropdown"
            >
              {dateFilterOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="activities-list">
          {sonPuantajlar.length > 0 ? (
            sonPuantajlar.map((puantaj, index) => (
              <div key={index} className="activity-item">
                <div className="activity-icon">
                  <Calendar size={16} />
                </div>
                <div className="activity-content">
                  <p className="activity-title">
                    {puantaj['Müşteri Adı'] || 'Bilinmeyen Müşteri'} - {puantaj['Makine İsmi'] || 'Bilinmeyen Makine'}
                  </p>
                  <p className="activity-subtitle">
                    {puantaj.Tarih || 'Tarih Yok'} • {puantaj['Operatör Adı'] || 'Bilinmeyen Operatör'}
                  </p>
                  <div className="activity-time-row">
                    <p className="activity-time">
                      {puantaj['1. Başlangıç Saati'] && puantaj['1. Bitiş Saati'] && 
                        `${puantaj['1. Başlangıç Saati']} - ${puantaj['1. Bitiş Saati']}`}
                      {puantaj['2. Başlangıç Saati'] && puantaj['2. Bitiş Saati'] && 
                        ` | ${puantaj['2. Başlangıç Saati']} - ${puantaj['2. Bitiş Saati']}`}
                      {!puantaj['1. Başlangıç Saati'] && !puantaj['2. Başlangıç Saati'] && 'Saat bilgisi yok'}
                    </p>
                    <p className="activity-total-hours">
                      Toplam: {formatTotalWorkTime(puantaj)} Saat
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-activities">
              <p>Henüz puantaj kaydı bulunmuyor.</p>
            </div>
          )}
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <Calendar size={24} />
          </div>
          <div className="stat-content">
            <h3>Toplam Puantaj</h3>
            <p className="stat-number">{stats.totalPuantajlar}</p>
            <span className="stat-label">Kayıt</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <h3>Son Hafta</h3>
            <p className="stat-number">{stats.sonHaftaPuantajlar}</p>
            <span className="stat-label">Puantaj</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <h3>Toplam Saat</h3>
            <p className="stat-number">{stats.toplamSaat}</p>
            <span className="stat-label">Saat</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <h3>Son Hafta Saat</h3>
            <p className="stat-number">{stats.sonHaftaSaat}</p>
            <span className="stat-label">Saat</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <h3>Toplam Kontör</h3>
            <p className="stat-number">{stats.toplamKontor.toLocaleString()}</p>
            <span className="stat-label">Dakika</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <h3>Son Hafta Kontör</h3>
            <p className="stat-number">{stats.sonHaftaKontor.toLocaleString()}</p>
            <span className="stat-label">Dakika</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <h3>Aktif Operatörler</h3>
            <p className="stat-number">{stats.aktifOperatorler}</p>
            <span className="stat-label">Operatör</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Building2 size={24} />
          </div>
          <div className="stat-content">
            <h3>Aktif Müşteriler</h3>
            <p className="stat-number">{stats.aktifMusteriler}</p>
            <span className="stat-label">Müşteri</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Wrench size={24} />
          </div>
          <div className="stat-content">
            <h3>Aktif Makineler</h3>
            <p className="stat-number">{stats.aktifMakineler}</p>
            <span className="stat-label">Makine</span>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">
            <Calendar size={24} />
          </div>
          <div className="stat-content">
            <h3>Bekleyen Onaylar</h3>
            <p className="stat-number">{stats.bekleyenOnaylar}</p>
            <span className="stat-label">Onay</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnaSayfa; 